import { useState, useCallback } from 'react';

const AUTH_KEY = 'premiado_auth';

export interface AuthSession {
  isAuthenticated: boolean;
  userId?: string;
  nome?: string;
  email?: string;
  loginAt?: string;
  // JWT tokens from real API
  accessToken?: string;
  refreshToken?: string;
  sessionId?: string;
  expiresIn?: number;
  tokenType?: string;
  riskLevel?: string;
  deviceFingerprint?: string;
}

export function useAuth() {
  const [session, setSession] = useState<AuthSession>(() => {
    try {
      const stored = localStorage.getItem(AUTH_KEY);
      if (stored) {
        return JSON.parse(stored) as AuthSession;
      }
    } catch {
      // ignore
    }
    return { isAuthenticated: false };
  });

  const login = useCallback((data: Omit<AuthSession, 'isAuthenticated' | 'loginAt'>) => {
    const newSession: AuthSession = {
      ...data,
      isAuthenticated: true,
      loginAt: new Date().toISOString(),
    };
    localStorage.setItem(AUTH_KEY, JSON.stringify(newSession));
    setSession(newSession);
  }, []);

  const logout = useCallback(async () => {
    const stored = localStorage.getItem(AUTH_KEY);
    if (stored) {
      try {
        const s = JSON.parse(stored) as AuthSession;
        if (s.accessToken) {
          const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '';
          const payload = {
            login: s.userId ?? '',
            deviceFingerprint: s.deviceFingerprint ?? '',
          };

          // eslint-disable-next-line no-console
          console.log('[LOGOUT] Payload enviado para /api/v1/auth/logout:', JSON.stringify(payload, null, 2));

          const response = await fetch(`${API_BASE}/api/v1/auth/logout`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${s.accessToken}`,
            },
            body: JSON.stringify(payload),
          });

          // eslint-disable-next-line no-console
          console.log('[LOGOUT] Resposta da API (status', response.status, '):', await response.text().catch(() => 'N/A'));
        }
      } catch {
        // ignora erros da API de logout e limpa localmente mesmo assim
      }
    }

    localStorage.removeItem(AUTH_KEY);
    setSession({ isAuthenticated: false });
  }, []);

  const isAuthenticated = session.isAuthenticated === true;

  return { session, isAuthenticated, login, logout };
}

export function getAuthSession(): AuthSession {
  try {
    const stored = localStorage.getItem(AUTH_KEY);
    if (stored) {
      return JSON.parse(stored) as AuthSession;
    }
  } catch {
    // ignore
  }
  return { isAuthenticated: false };
}

export function getAccessToken(): string | null {
  const session = getAuthSession();
  return session.accessToken ?? null;
}